<template>
  <form @submit.prevent="submitForm" class="vf-form">
    <!-- ==================== Identity ==================== -->
    <section class="vf-section">
      <div class="section-head">
        <div class="section-title">Identity</div>
        <div class="section-sub">Display name and login username.</div>
      </div>
      <div class="section-grid">
        <div class="vf-field" :class="{ 'is-invalid': !name.isValid }">
          <label for="name">Name</label>
          <input
            type="text"
            id="name"
            v-model.trim="name.val"
            @blur="clearValidity('name')"
            class="vf-input"
            placeholder="e.g. Amit Kumar"
          />
          <p v-if="!name.isValid" class="vf-error">Name must not be empty.</p>
        </div>

        <div class="vf-field" :class="{ 'is-invalid': !username.isValid }">
          <label for="username">Username</label>
          <div v-if="mode == 'update'" class="vf-locked">
            <span class="vf-locked-val">{{ username.val }}</span>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="vf-lock">
              <rect x="4" y="11" width="16" height="10" rx="2" />
              <path d="M8 11V7a4 4 0 018 0v4" />
            </svg>
          </div>
          <input
            v-else
            type="text"
            id="username"
            v-model.trim="username.val"
            @blur="clearValidity('username')"
            class="vf-input"
            placeholder="login username"
          />
          <p v-if="!username.isValid" class="vf-error">Username must not be empty.</p>
        </div>
      </div>
    </section>

    <!-- ==================== Contact ==================== -->
    <section class="vf-section">
      <div class="section-head">
        <div class="section-title">Contact</div>
        <div class="section-sub">How ops can reach this vendor.</div>
      </div>
      <div class="section-grid">
        <div class="vf-field" :class="{ 'is-invalid': !email.isValid }">
          <label for="email">Email</label>
          <input
            type="email"
            id="email"
            v-model.trim="email.val"
            @blur="clearValidity('email')"
            class="vf-input"
            placeholder="ops@vendor.com"
          />
          <p v-if="!email.isValid" class="vf-error">Email must not be empty.</p>
        </div>

        <div class="vf-field" :class="{ 'is-invalid': !phone.isValid }">
          <label for="phone">Phone</label>
          <input
            type="text"
            id="phone"
            v-model.trim="phone.val"
            maxlength="10"
            @blur="clearValidity('phone')"
            @keypress="isNumber($event)"
            class="vf-input"
            placeholder="10-digit number"
          />
          <p v-if="!phone.isValid" class="vf-error">{{ phone.msg }}</p>
        </div>
      </div>
    </section>

    <!-- ==================== Access ==================== -->
    <section class="vf-section">
      <div class="section-head">
        <div class="section-title">Access</div>
        <div class="section-sub">
          {{ mode === 'update' ? 'Leave password empty to keep the current one unchanged.' : 'Password used for the vendor login.' }}
        </div>
      </div>
      <div class="section-grid">
        <div class="vf-field vf-field-full" :class="{ 'is-invalid': !password.isValid }">
          <label for="password">Password</label>
          <input
            type="text"
            id="password"
            v-model.trim="password.val"
            @blur="clearValidity('password')"
            class="vf-input"
            :placeholder="mode === 'update' ? 'leave empty to keep unchanged' : 'set a strong password'"
          />
          <p v-if="!password.isValid" class="vf-error">Password must not be empty.</p>
        </div>
      </div>
    </section>

    <!-- ==================== Session state (update-only) ==================== -->
    <section v-if="mode == 'update'" class="vf-section">
      <div class="section-head">
        <div class="section-title">Session State</div>
        <div class="section-sub">Manage the vendor's active status and forced logout.</div>
      </div>
      <div class="section-grid">
        <div class="vf-field">
          <label for="status">Status</label>
          <select v-model.trim="status.val" id="status" class="vf-input">
            <option :value="1">Active</option>
            <option :value="0">Inactive</option>
          </select>
        </div>
        <div class="vf-field">
          <label for="isLoggedIn">Login Status</label>
          <select v-model.trim="isLoggedIn.val" id="isLoggedIn" class="vf-input">
            <option :value="1">Logged In</option>
            <option :value="0">Logged Out</option>
          </select>
        </div>
      </div>
    </section>

    <!-- ==================== Self-serve grant ==================== -->
    <!-- Vendor self-serve toggle. When on, the vendor can access BOTH
         /clients AND /admin-report (and the new /ledger) from their sidebar
         and API. Ops asked for a single grant rather than independent
         toggles — historical label was "clients" but the flag gates every
         vendor-only surface. -->
    <section class="vf-section">
      <div class="section-head">
        <div class="section-title">Self-Serve</div>
        <div class="section-sub">Allow this vendor to manage their own clients, Admin Report, and Ledger.</div>
      </div>
      <label class="vf-toggle-row" :class="{ 'is-on': isSelfServeOn, 'is-off': !isSelfServeOn }">
        <div class="vf-toggle-copy">
          <div class="vf-toggle-title">
            Self-serve
            <span :class="isSelfServeOn ? 'vf-toggle-badge vf-toggle-badge-on' : 'vf-toggle-badge vf-toggle-badge-off'">
              {{ isSelfServeOn ? 'GRANTED' : 'RESTRICTED' }}
            </span>
          </div>
          <div class="vf-toggle-desc">
            When granted, /clients, /admin-report and /ledger become available in this vendor's sidebar and API.
          </div>
        </div>
        <span class="vf-switch" @click.prevent="toggleSelfServe">
          <input type="checkbox" :checked="isSelfServeOn" class="vf-switch-input" @change.prevent />
          <span class="vf-switch-track"></span>
          <span class="vf-switch-knob"></span>
        </span>
      </label>
    </section>

    <!-- ==================== Submit ==================== -->
    <div class="vf-submit">
      <p v-if="!formIsValid" class="vf-submit-error">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4M12 16h.01" stroke-linecap="round" />
        </svg>
        Please fix the highlighted errors and submit again.
      </p>
      <button type="submit" class="btn btn-primary">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" class="btn-icon">
          <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        {{ mode === 'create' ? 'Create Vendor' : 'Save Changes' }}
      </button>
    </div>
  </form>
</template>

<script>
export default {
  name: "vendor-user-form",
  emits: ["create-user"],
  props: ["mode", "selectedUser"],
  mounted() {
    if (this.mode == "update") {
      this.setUser();
    }
  },
  data() {
    return {
      name: { val: "", isValid: true, msg: "" },
      username: { val: "", isValid: true, msg: "" },
      email: { val: "", isValid: true, msg: "" },
      phone: { val: "", isValid: true, msg: "" },
      password: { val: "", isValid: true, msg: "" },
      role: { val: "admin", isValid: true, msg: "" },
      status: { val: 1, isValid: true, msg: "" },
      isLoggedIn: { val: 0, isValid: true, msg: "" },
      canManageClients: { val: 0, isValid: true, msg: "" },
      formIsValid: true,
    };
  },
  computed: {
    isSelfServeOn() {
      return Number(this.canManageClients.val) === 1;
    },
  },
  methods: {
    toggleSelfServe() {
      this.canManageClients.val = this.isSelfServeOn ? 0 : 1;
    },
    clearValidity(input) {
      this[input].isValid = true;
      this[input].msg = "";
    },
    validateForm() {
      this.formIsValid = true;

      if (this.mode == "create") {
        if (this.username.val === "") {
          this.username.isValid = false;
          this.formIsValid = false;
        }
        if (this.password.val === "") {
          this.password.isValid = false;
          this.formIsValid = false;
        }
      }

      if (this.name.val === "") {
        this.name.isValid = false;
        this.formIsValid = false;
      }
      if (this.email.val === "") {
        this.email.isValid = false;
        this.formIsValid = false;
      }
      if (this.phone.val === "") {
        this.phone.isValid = false;
        this.phone.msg = "Phone must not be empty.";
        this.formIsValid = false;
      } else if (this.phone.val.length < 10) {
        this.phone.isValid = false;
        this.phone.msg = "Phone number must be 10 digits.";
        this.formIsValid = false;
      }
    },
    submitForm() {
      this.validateForm();

      if (!this.formIsValid) {
        return;
      }

      let formData = {
        username: this.username.val,
        name: this.name.val,
        email: this.email.val,
        phone: this.phone.val,
        role: this.role.val,
        status: this.status.val,
        isLoggedIn: this.isLoggedIn.val,
        can_manage_clients: Number(this.canManageClients.val) === 1 ? 1 : 0,
      };

      if (this.mode == "create") {
        formData.password = this.password.val;
      } else {
        if (this.password.val != "") {
          formData.password = this.password.val;
        }
      }

      this.$emit("create-user", formData);
    },
    isNumber: function(evt) {
      evt = evt ? evt : window.event;
      var charCode = evt.which ? evt.which : evt.keyCode;
      if (
        charCode > 31 &&
        (charCode < 48 || charCode > 57) &&
        charCode !== 46
      ) {
        evt.preventDefault();
      } else {
        return true;
      }
    },
    setUser() {
      this.name.val = this.selectedUser.name;
      this.username.val = this.selectedUser.username;
      this.email.val = this.selectedUser.email;
      this.phone.val = this.selectedUser.phone;
      this.role.val = "admin";
      this.status.val = Number(this.selectedUser.status) === 1 ? 1 : 0;
      this.isLoggedIn.val = Number(this.selectedUser.isLoggedIn) === 1 ? 1 : 0;
      this.canManageClients.val = Number(this.selectedUser.can_manage_clients) === 1 ? 1 : 0;
    },
  },
};
</script>

<style scoped>
/* Form shell */
.vf-form {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 4px 20px 20px;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
}

/* Section card */
.vf-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 20px 22px;
}
.section-head { margin-bottom: 16px; }
.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.01em;
}
.section-sub {
  font-size: 12px;
  color: #64748b;
  margin-top: 3px;
}

/* Grid */
.section-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px 20px;
}
@media (max-width: 720px) {
  .section-grid { grid-template-columns: 1fr; }
}

/* Field */
.vf-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.vf-field-full { grid-column: 1 / -1; }
.vf-field label {
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

/* Input */
.vf-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  font-size: 13px;
  background: #f8fafc;
  outline: none;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
  font-family: inherit;
  color: #0f172a;
}
.vf-input::placeholder { color: #94a3b8; }
.vf-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
select.vf-input {
  appearance: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='none' stroke='%2364748b' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M1 1l5 5 5-5'/></svg>");
  background-repeat: no-repeat;
  background-position: right 12px center;
  padding-right: 32px;
}

/* Invalid */
.vf-field.is-invalid .vf-input,
.vf-field.is-invalid .vf-locked {
  border-color: #ef4444;
  background: #fef2f2;
}
.vf-field.is-invalid .vf-input:focus {
  box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.14);
}
.vf-error {
  font-size: 11px;
  color: #dc2626;
  margin-top: 2px;
  font-weight: 500;
}

/* Locked (read-only display) */
.vf-locked {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f1f5f9;
  color: #475569;
  font-size: 13px;
}
.vf-locked-val {
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.vf-lock {
  width: 14px; height: 14px;
  color: #94a3b8;
  flex-shrink: 0;
}

/* Toggle row */
.vf-toggle-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.15s;
}
.vf-toggle-row.is-on {
  border-color: #a7f3d0;
  background: #ecfdf5;
}
.vf-toggle-row.is-off {
  border-color: #e2e8f0;
  background: #f8fafc;
}
.vf-toggle-copy { min-width: 0; }
.vf-toggle-title {
  font-size: 14px;
  font-weight: 600;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.vf-toggle-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
}
.vf-toggle-badge-on { background: #d1fae5; color: #065f46; }
.vf-toggle-badge-off { background: #f1f5f9; color: #64748b; }
.vf-toggle-desc {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

/* iOS-style switch */
.vf-switch {
  position: relative;
  display: inline-block;
  width: 44px; height: 24px;
  flex-shrink: 0;
  cursor: pointer;
}
.vf-switch-input {
  opacity: 0;
  width: 0;
  height: 0;
  pointer-events: none;
  position: absolute;
}
.vf-switch-track {
  position: absolute;
  inset: 0;
  border-radius: 999px;
  background: #cbd5e1;
  transition: background 0.18s;
}
.vf-switch-knob {
  position: absolute;
  top: 2px; left: 2px;
  width: 20px; height: 20px;
  border-radius: 999px;
  background: #fff;
  box-shadow: 0 2px 4px rgba(15, 23, 42, 0.2);
  transition: transform 0.18s;
}
.vf-switch-input:checked ~ .vf-switch-track { background: #10b981; }
.vf-switch-input:checked ~ .vf-switch-knob { transform: translateX(20px); }

/* Submit */
.vf-submit {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 10px;
  padding: 8px 0 4px;
}
.vf-submit-error {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #b91c1c;
  font-size: 12px;
  font-weight: 500;
  align-self: stretch;
  justify-content: center;
}
.vf-submit-error svg { width: 14px; height: 14px; }

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 22px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
}
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-icon { width: 15px; height: 15px; }
</style>
